import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { BRAND_ASSETS, BrandMark } from '@/features/about/BrandMark';
import { z } from 'zod';
import { Link, useLocation, useNavigate } from 'react-router';
import { useLogin } from '@/shared/api/session';
import { errorMessage } from '@/shared/api/client';
import { Alert, Button, Card, Field, Input } from '@/shared/ui';
import { routeForState } from '@/app/guards';

const schema = z.object({ email: z.email('Correo inválido'), password: z.string().min(1, 'Ingresa tu contraseña') });
type Form = z.infer<typeof schema>;

export function LoginPage() {
  const login = useLogin();
  const navigate = useNavigate();
  const loc = useLocation() as { state?: { from?: string } };
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<Form>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit(async (values) => {
    try {
      const s = await login.mutateAsync(values);
      navigate(routeForState(s.state) ?? loc.state?.from ?? '/', { replace: true });
    } catch { /* el error se muestra abajo */ }
  });

  return (
    <Card title="Ingresar">
      {login.isError && <Alert kind="error">{errorMessage(login.error)}</Alert>}
      <form onSubmit={onSubmit} noValidate>
        <Field label="Correo corporativo" htmlFor="email" error={errors.email?.message}>
          <Input id="email" type="email" autoComplete="username" inputMode="email" invalid={!!errors.email} {...register('email')} />
        </Field>
        <Field label="Contraseña" htmlFor="password" error={errors.password?.message}>
          <Input id="password" type="password" autoComplete="current-password" invalid={!!errors.password} {...register('password')} />
        </Field>
        <Button type="submit" block loading={isSubmitting || login.isPending}>Ingresar</Button>
      </form>
      <p style={{ marginTop: 'var(--sp-4)', fontSize: 'var(--fs-sm)', textAlign: 'center' }}><Link to="/auth/reset-request">¿Olvidaste tu contraseña?</Link></p>
      <OpenAiNote />
    </Card>
  );
}

/** Identificación discreta y secundaria del uso de OpenAI para el análisis documental (no es branding ni patrocinio).
 *  El isotipo oficial es opcional y vive en /brand; si no está, queda solo el texto (ver web/public/brand/README.md). */
function OpenAiNote() {
  return (
    <p style={{ marginTop: 'var(--sp-5)', paddingTop: 'var(--sp-3)', borderTop: '1px solid var(--sgr-border)', textAlign: 'center', fontSize: 'var(--fs-xs)', color: 'var(--sgr-text-muted)', lineHeight: 1.6 }}>
      Análisis documental asistido por{' '}
      {/* el isotipo va pegado al nombre: si la línea se parte, no se separan */}
      <span style={{ whiteSpace: 'nowrap' }}>
        <BrandMark src={BRAND_ASSETS.openai} name="OpenAI" height={12} inline />{' '}OpenAI
      </span>
    </p>
  );
}
